/** @jsxImportSource @emotion/react */
import { css, useTheme } from "@emotion/react";
import { useLanguage } from "LanguageContext";
import { useEffect, useState } from "react";
import videoOver from "../../../assets/videoOver.svg";

function YoutubePreview({
  youtubeurl,
  showDes,
}: {
  youtubeurl: string;
  showDes: boolean;
}) {
  const { t } = useLanguage();
  const theme = useTheme();
  const [videoId, setVideoId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function extractYouTubeId(input: string): string | null {
    if (!input) return null;
    const url = input.trim().replace(/^https?:\/\//i, ""); // remove protocol if present

    const patterns = [
      /youtu\.be\/([A-Za-z0-9_-]{11})/, // youtu.be/ID
      /youtube\.com\/.*v=([A-Za-z0-9_-]{11})/, // youtube.com/watch?v=ID
      /youtube\.com\/embed\/([A-Za-z0-9_-]{11})/, // youtube.com/embed/ID
      /youtube\.com\/shorts\/([A-Za-z0-9_-]{11})/, // youtube.com/shorts/ID
      /youtube\.com\/v\/([A-Za-z0-9_-]{11})/, // youtube.com/v/ID
    ];

    for (const p of patterns) {
      const m = url.match(p);
      if (m && m[1]) return m[1];
    }

    // fallback: try to pick 11-char id anywhere (less strict)
    const fallback = url.match(/([A-Za-z0-9_-]{11})/);
    return fallback ? fallback[1] : null;
  }

  function buildThumbnailUrls(videoId: string) {
    return {
      maxres: `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`,
      hq: `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
      mq: `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`,
      default: `https://img.youtube.com/vi/${videoId}/default.jpg`,
    };
  }

  const loadUrl = () => {
    const id = extractYouTubeId(youtubeurl);
    if (!id) {
      setVideoId(null);
      setError("Invalid YouTube URL or ID");
    } else {
      setVideoId(id);
      setError(null);
    }
  };

  useEffect(() => {
    youtubeurl && loadUrl();
  }, [youtubeurl]);

  const thumbnails = videoId ? buildThumbnailUrls(videoId) : null;

  const msg = css`
    font-family: Font/Family/Volte;
    font-weight: 500;
    font-size: 14px;
    line-height: 150%;
    letter-spacing: 0%;
  `;

  const playbackWrapper = css`
    position: relative;
    max-height: 150px;
    width: 248px;
    border: 1px solid ${theme.colors.strokeGray};
    padding: ${theme.spacing.md};
    border-radius: ${theme.radius.sm};
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    cursor: pointer;
  `;

  const imgthumbnails = css`
    width: 228px;
    max-height: 150px;
    opacity: 1;
    border-radius: 8px;
  `;

  const youtubeImg = css`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 64px;
    height: 64px;
    cursor: pointer;
    opacity: 0.7;
  `;

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      {error && <div style={{ color: "red", marginTop: 8 }}>{error}</div>}

      {thumbnails?.maxres && (
        <div css={playbackWrapper}>
          <img
            src={thumbnails.maxres}
            alt="maxres thumbnail"
            css={imgthumbnails}
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).src = thumbnails.hq;
            }}
          />
          <img src={videoOver} alt="Play" css={youtubeImg} />
        </div>
      )}

      {showDes && <p css={msg}>{t("form_helpTxtYoutube")}</p>}
    </div>
  );
}

export default YoutubePreview;